import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { withRoomContext } from '../../../RoomContext';

import Avatar from '@material-ui/core/Avatar';
import { useIntl } from 'react-intl';
import IconButton from '@material-ui/core/IconButton';
import SendIcon from '@material-ui/icons/Send';

import { firestore } from '../../../lib/firebase';

import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';

const styles = (theme) =>
	({
		root :
		{
			flexGrow        : 1,
			maxWidth        : 500,
			width           : '100%',
			height          : '100%',
			borderRadius    : '0.5rem',
			boxShadow       : 'none',
			backgroundColor : 'transparent'
		},
		title :
		{
			color          : '#878686',
			fontSize       : '1rem',
			display        : 'flex',
			alignItems     : 'center',
			justifyContent : 'space-between',
			marginBottom   : '5%',
			padding        : '0 22%',
			whiteSpace     : 'nowrap'
		},
		body :
		{
			backgroundColor : 'white',
			height          : '100%',
			padding         : '4%',
			justifyContent  : 'end',
			borderRadius    : '2rem'
		},
		chatList :
		{
			height        : 'calc(100% - 3rem)',
			display       : 'flex',
			flexDirection : 'column',
			overflowY     : 'auto'
		},
		chat :
		{
			borderRadius    : '0.4rem',
			backgroundColor : '#dbdbd8',
			display         : 'flex',
			justifyContent  : 'center',
			alignItems      : 'flex-start',
			padding         : '0.6rem',
			marginBottom    : '0.3rem',
			color           : '#808080'
		},
		chatPeer :
		{
			backgroundColor : '#9ec317',
			color           : 'white'
		},
		chatAvatar :
		{
			width          : '15%',
			display        : 'flex',
			justifyContent : 'center',
			alignItems     : 'center'
		},
		avatar :
		{
			width           : '2.5rem',
			height          : '2.5rem',
			backgroundColor : '#fff',
			padding         : '20%',
			'&> img'        :
			{
				objectFit : 'contain'
			}
		},
		avatarMe :
		{
		},
		chatMessage :
		{
			width     : '85%',
			textAlign : 'left',
			padding   : '0 5%'
		},
		chatMessageName :
		{
			fontSize     : '0.7rem',
			marginBottom : '0.1rem'
		},
		chatMessageText :
		{
			fontSize     : '0.8rem',
			whiteSpace   : 'pre-wrap',
			overflowWrap : 'break-word'
		},
		input :
		{
			display        : 'flex',
			justifyContent : 'flex-start',
			alignItems     : 'center',
			position       : 'relative',
			marginTop      : '0.7rem'
		},
		inputBase :
		{
			width    : `calc(100% - ${theme.spacing(5.5)}px)`,
			position : 'absolute',
			bottom   : 1
		},
		textArea : {
			flex                        : 1,
			borderRadius                : '0.5rem',
			padding                     : '3% 5%',
			fontSize                    : '0.84rem',
			width                       : '100%',
			background                  : 'white',
			'& $textAreaNotchedOutline' : {
				borderColor : 'var(--text-color)'
			},
			'&:hover $textAreaNotchedOutline' : {
				borderColor : 'var(--text-color)'
			},
			'&.Mui-focused $textAreaNotchedOutline' : {
				borderColor : 'var(--text-color)',
				borderWidth : '1px'
			},
			[theme.breakpoints.down('lg')] : {
				padding : '4% 5%'
			}
		},
		textAreaNotchedOutline : {
			borderColor : 'var(--text-color)'
		},
		action : {
			width          : '100%',
			display        : 'flex',
			justifyContent : 'flex-end'
		},
		iconButton : {
			padding         : 6,
			backgroundColor : 'var(--text-color)',
			color           : 'white',
			'&:hover'       : {
				backgroundColor : 'var(--text-color)',
				color           : 'white'
			}
		},
		noMessage : {
			color     : '#878686',
			marginTop : '100%',
			textAlign : 'center'
		}
	});

const TeamChatView = ({
	room,
	user,
	groupChatHistory,
	classes
}) =>
{
	const [ message, setMessage ] = useState('');

	const handleChangeMessage = useCallback((e) =>
	{
		if (e)
		{
			setMessage(e.target.value);
		}
		else
		{
			setMessage('');
		}
	}, [ setMessage ]);

	const intl = useIntl();

	const scrollBottomRef = useRef();

	const sendMessage =useCallback(() =>
	{
		if (user && room.info)
		{
			firestore.collection('chats')
				.add({
					uid       : user.uid,
					name      : room.info.user,
					avatarUrl : '',
					message   : message,
					roomId    : room.name,
					timestamp : Date.now()
				});
		}
	}, [ user, room.info, message, room.name ]);

	useLayoutEffect(() =>
	{
		scrollBottomRef?.current?.scrollIntoView(false);
	}, [ groupChatHistory ]);

	const sendMessageByKeydown = useCallback((e) =>
	{
		if (e.shiftKey)
		{
			if (e.keyCode === 13)
			{
				e.preventDefault();

				sendMessage();

				handleChangeMessage(null);
			}
		}

	}, [ sendMessage, handleChangeMessage ]);

	useEffect(() =>
	{
		document.addEventListener('keydown', sendMessageByKeydown);

		return () =>
		{
			// Clean up the subscription
			document.removeEventListener('keydown', sendMessageByKeydown);
		};
	}, [ sendMessageByKeydown ]);

	return (
		<div className={classes.root}>
			<div className={classes.body}>
				<div className={classes.chatList}>
					{(!groupChatHistory || groupChatHistory.length < 1) ? (
						<div className={classes.noMessage}>メッセージはありません</div>
					) : (
						groupChatHistory.map((item, index) =>
							(
								<div
									className={classnames(
										classes.chat,
										item.roomId === room.name ? classes.chatPeer : null
									)}
									key={index}
								>
									<div className={classes.chatAvatar}>
										<Avatar
											alt={item.name}
											src={item.roomId === room.name ? 'images/icon_host_groupchat_me.svg' : 'images/icon_host_groupchat_peer.svg'}
											className={classes.avatar}
										>
											{item.name.substring(0, 1)}
										</Avatar>
									</div>
									<div className={classes.chatMessage}>
										<div className={classes.chatMessageName}>
											{item.name}
										</div>
										<div className={classes.chatMessageText}>
											{item.message}
										</div>
									</div>
								</div>
							)
						))}
					<div ref={scrollBottomRef}/>
				</div>
				<div className={classes.input}>
					<TextField
						classes={{
							root : classes.inputBase
						}}
						InputProps={{
							classes : {
								root           : classes.textArea,
								notchedOutline : classes.textAreaNotchedOutline
							}
						}}
						variant='outlined'
						placeholder={intl.formatMessage({
							id             : 'label.chatInput',
							defaultMessage : 'Enter chat message...'
						})}
						value={message || ''}
						multiline
						minRows={1}
						onChange={handleChangeMessage}
						autoFocus
					/>
					<Box className={classes.action}>
						<IconButton
							color='primary'
							className={classes.iconButton}
							aria-label='送信'
							onClick={() =>
							{
								if (message && message !== '')
								{
									sendMessage();

									handleChangeMessage(null);
								}
							}}
						>
							<SendIcon />
						</IconButton>
					</Box>
				</div>
			</div>
		</div>
	);

};

TeamChatView.propTypes =
{
	room             : PropTypes.object.isRequired,
	user             : PropTypes.object.isRequired,
	classes          : PropTypes.object.isRequired,
	groupChatHistory : PropTypes.array,
	titleIcon        : PropTypes.element
};

const mapStateToProps = (state) =>
{
	return {
		room             : state.room,
		user             : state.me.user,
		loginEnabled     : state.me.loginEnabled,
		loggedIn         : state.me.loggedIn,
		groupChatHistory : state.room.groupChatHistory
	};
};

const mapDispatchToProps = () =>
{
	return {
	};
};

export default withRoomContext(connect(
	mapStateToProps,
	mapDispatchToProps,
	null,
	{
		areStatesEqual : (next, prev) =>
		{
			return (
				prev.room === next.room &&
				prev.me.user === next.me.user &&
				prev.me.loginEnabled === next.me.loginEnabled &&
				prev.me.loggedIn === next.me.loggedIn &&
				prev.chat === next.chat
			);
		}
	}
)(withStyles(styles)(TeamChatView)));
